import React, { useState } from "react";
import { Button, Empty, Input } from "antd";
import { AppstoreOutlined, PlusOutlined } from "@ant-design/icons";
import CreateMenu from "../components/menus/CreateMenu";
import MenuTable from "../components/menus/MenuTable";
import useMenu from "../hooks/useMenu";
import { nowDate } from "../components/Customs/nowDate";
import { notify } from "../components/Customs/NotifyProvider";
import {
  canViewModule,
  canCreateModule,
  canUpdateModule,
  canDeleteModule,
} from "../utils/permissionUtils";

const MODULE_KEY = "manage_menu";

const MenuPage = () => {
  const { menus, fetchMenus, loading, createMenu, updateMenu, deleteMenu, cat } = useMenu();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingMenu, setEditingMenu] = useState(null);
  const [searchText, setSearchText] = useState("");

  if (!canViewModule(MODULE_KEY)) {
    return (
      <div className="page-shell user-master-page">
        <div className="page-empty">
          <Empty description="No permission to view menu page" />
        </div>
      </div>
    );
  }

  const filteredMenus = menus.filter((menu) =>
    Object.values(menu).some((val) =>
      String(val).toLowerCase().includes(searchText.toLowerCase())
    )
  );

  const openCreate = () => {
    if (!canCreateModule(MODULE_KEY)) {
      notify.error("Permission Denied", "No permission to create menu");
      return;
    }

    setEditingMenu(null);
    setDrawerOpen(true);
  };

  const openEdit = (menu) => {
    if (!canUpdateModule(MODULE_KEY)) {
      notify.error("Permission Denied", "No permission to update menu");
      return;
    }

    setEditingMenu(menu);
    setDrawerOpen(true);
  };

  const handleDelete = async (id) => {
    if (!canDeleteModule(MODULE_KEY)) {
      notify.error("Permission Denied", "No permission to delete menu");
      return;
    }

    await deleteMenu("menu_items", id);
  };

  const handleSubmit = async (values) => {
    const req = {
      ...values,
      created_at: nowDate(),
      updated_at: nowDate(),
    };

    const reqUpdate = {
      ...values,
      updated_at: nowDate(),
    };

    if (editingMenu) {
      if (!canUpdateModule(MODULE_KEY)) {
        notify.error("Permission Denied", "No permission to update menu");
        return;
      }

      await updateMenu("menu_items", editingMenu.id, reqUpdate);
      fetchMenus();
    } else {
      if (!canCreateModule(MODULE_KEY)) {
        notify.error("Permission Denied", "No permission to create menu");
        return;
      }

      await createMenu("menu_items", req);
      fetchMenus();
    }

    setDrawerOpen(false);
  };

  return (
    <div className="page-shell user-master-page">
      <div className="user-page-header">
        <div className="user-page-header-left">
          <div className="user-page-icon">
            <AppstoreOutlined />
          </div>

          <div>
            <h2 className="user-page-title">Manage Menu</h2>
            <p className="user-page-subtitle">
              Create, update, and manage all menu items from one place.
            </p>
          </div>
        </div>

        <div className="user-page-header-actions" style={{ display: "flex", alignItems: "center" }}>
          <Input
            placeholder="Search menu..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            size="large"
            style={{ width: 350, marginRight: 10 }}
          />

          <Button
            type="primary"
            className="primary-btn"
            icon={<PlusOutlined />}
            onClick={openCreate}
            disabled={!canCreateModule(MODULE_KEY)}
          >
            Create Menu
          </Button>
        </div>
      </div>

      <div className="page-card table-card">
        <div className="table-card-header">
          <div>
            <h3>Menu List</h3>
            <p>View and manage available menu items.</p>
          </div>
        </div>

        <MenuTable
          data={filteredMenus}
          loading={loading}
          onEdit={openEdit}
          onDelete={handleDelete}
          cat={cat}
          canUpdate={canUpdateModule(MODULE_KEY)}
          canDelete={canDeleteModule(MODULE_KEY)}
        />
      </div>

      <CreateMenu
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onSubmit={handleSubmit}
        initialValues={editingMenu}
        cat={cat}
      />
    </div>
  );
};

export default MenuPage;